import './style.css'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import { addCameraDebug, addDirectionalLightDebug, addModelDebug, gui } from './debug'
import { Color, Group, MeshLambertMaterial, PlaneBufferGeometry, Vector2 } from 'three'
import { gsap } from 'gsap'

/**
 * Base
 */
const isProduction = process.env.NODE_ENV === 'production'
let camera, scene, renderer, mixer, controls, clock, model, sphere, floor, cloudGroup, overlayMaterial;
let previousTime = 0

const sizes = {
  width: window.innerWidth,
  height: window.innerHeight
}
/**
 * mouse
 */
const mouse = new Vector2()


window.attachHeartCanvas = (selector, options = {}) => {
  const container = document.querySelector(selector);

  if (container) {
    init(container, options)
    eventListeners()

  } else {
    console.error("selector doesn't match any element in the document")
  }
}
window.attachHeartCanvas('#canvas-container', { loadingBar: true })

function init(container, options) {
  clock = new THREE.Clock();
  renderer = new THREE.WebGLRenderer();
  sizes.width = container.offsetWidth
  sizes.height = container.offsetHeight

  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = THREE.PCFSoftShadowMap
  renderer.setSize(sizes.width, sizes.height)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  container.appendChild(renderer.domElement);

  // Scene
  scene = new THREE.Scene()

  // Base camera
  camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
  camera.position.set(0.01, .6, 1)
  scene.add(camera)


  /**
   * Overlay
   */
  if (options.loadingBar) {
    const overlayGeometry = new THREE.PlaneBufferGeometry(2, 2, 1, 1)
    overlayMaterial = new THREE.ShaderMaterial({
      // wireframe: true,
      transparent: true,
      uniforms:
      {
        uAlpha: { value: 1 }
      },
      vertexShader: `
      void main()
      {
        gl_Position = vec4(position, 1.0);
      }
      `,
      fragmentShader: `
      uniform float uAlpha;
      
      void main()
      {
        gl_FragColor = vec4(0.0, 0.0, 0.0, uAlpha);
      }
      `
    })
    const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial)
    overlay.position.z = .7
    scene.add(overlay)
  }
  /**
   * Models
   */
  const loadingBarElement = document.createElement('div')
  loadingBarElement.className = 'loading-bar'
  container.appendChild(loadingBarElement)
  let progress = 0;

  const loadingManager = !options.loadingBar ? null : new THREE.LoadingManager(
    // Loaded
    () => {
      // Wait a little
      gsap.delayedCall(.8, () => {
        gsap.to(overlayMaterial.uniforms.uAlpha, { duration: 2, value: 0, delay: .7 })

        // Update loadingBarElement
        loadingBarElement.classList.add('ended')
        loadingBarElement.style.transform = ''
        loadingBarElement.style.transition = ''
      })
    },
    // Progress
    (itemUrl, itemsLoaded, itemsTotal) => {
      const progressRatio = itemsLoaded / itemsTotal
      const newProgress = Math.max(progressRatio, progress)
      loadingBarElement.style.transition = `transform ${newProgress - progress}s`;
      progress = newProgress
      loadingBarElement.style.transform = `scaleX(${progress})`
    }
  )
  const gltfLoader = new GLTFLoader(options.loadingBar ? loadingManager : undefined)
  const textureLoader = new THREE.TextureLoader(options.loadingBar ? loadingManager : undefined)
  gltfLoader.setMeshoptDecoder(MeshoptDecoder);

  gltfLoader.load('/models/heart/DRAGONHEART123.gltf', (gltf) => {
    model = gltf.scene;
    model.scale.set(5, 5, 5)

    model.children[0].position.set(0, -1.1, 0);
    model.position.set(-.05, -1.4, .7)

    // Animation
    mixer = new THREE.AnimationMixer(model)
    const action = mixer.clipAction(gltf.animations[0])

    mixer.timeScale = 1.2

    gui.add(mixer, 'timeScale', -3, 3, .001)

    action.play()

    addModelDebug(model)
    addCameraDebug(camera, model.position)
    /**
     * Floor
     */
    floor = new THREE.Mesh(
      new THREE.PlaneBufferGeometry(2, 2),
      new THREE.MeshLambertMaterial({ map: textureLoader.load('/textures/bgk.jpeg') })
    )

    const group = new Group()
    // floor.receiveShadow = true
    group.add(floor, model)
    group.position.y += .6
    scene.add(group)

    // start animating
    tick()
  })

  /**
   * Lights
   */
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.8)
  scene.add(ambientLight)

  const directionalLight = new THREE.DirectionalLight(0xffffff, 0.6)
  // directionalLight.castShadow = true
  directionalLight.shadow.mapSize.set(1024, 1024)
  directionalLight.shadow.camera.far = 15
  directionalLight.shadow.camera.left = - 7
  directionalLight.shadow.camera.top = 7
  directionalLight.shadow.camera.right = 7
  directionalLight.shadow.camera.bottom = - 7
  directionalLight.position.set(0, 1, 1.5)
  scene.add(directionalLight)
  addDirectionalLightDebug(directionalLight);


  // Controls
  if (!isProduction) {
    // controls = new OrbitControls(camera, container)
    // controls.target.set(0, 0.75, 0)
    // controls.enableDamping = true
  }
  const cloudGeometry = new PlaneBufferGeometry(.4, .4)
  const cloudMaterial = new MeshLambertMaterial({
    alphaMap: textureLoader.load('/textures/smoke_06.png'),
    transparent: true,
  })
  cloudMaterial.depthWrite = false
  cloudMaterial.blending = THREE.AdditiveBlending
  cloudGroup = new Group()

  for (let p = 0; p < 40; p++) {
    const random = (Math.random() - .5)
    cloudGeometry.width += random
    cloudGeometry.height += random
    let cloud = new THREE.Mesh(cloudGeometry, cloudMaterial);
    cloud.position.set(
      Math.random() - .5,
      (Math.random() - .5) * .4,
      (Math.random() - .5) * .3
    );
    cloud.rotation.z = (Math.random() - .5) * 2 * Math.PI;
    cloudGroup.add(cloud)
  }

  cloudGroup.position.set(0, .55, .3)
  cloudGroup.rotation.y += Math.PI
  cloudGroup.rotation.x = 3

  cloudGroup.children[0].material.color = new Color('#23844F')
  scene.add(cloudGroup);

}

function eventListeners() {

  window.addEventListener('mousemove', (event) => {
    mouse.x = event.clientX / sizes.width * 2 - 1
    mouse.y = -(event.clientY / sizes.height) * 2 + 1
  })


  window.addEventListener('resize', () => {
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  })
}


/**
 * Animate
 */
function tick() {
  const elapsedTime = clock.getElapsedTime()
  const deltaTime = elapsedTime - previousTime
  previousTime = elapsedTime

  // Model animation
  if (mixer) {
    mixer.update(deltaTime)
  }

  // Update controls
  if (!isProduction && controls) {
    controls.update()
  }
  if (floor) {
    floor.rotation.x = mouse.y * 0.005
    model.rotation.x = -mouse.y * 0.005
  }
  if (cloudGroup) {
    cloudGroup.position.y = ((mouse.y + 1) * .04) + .35
    cloudGroup.children[0].material.opacity = .07 - (mouse.y + 1) / 25
  }

  // Render
  renderer.render(scene, camera)

  // Call tick again on the next frame
  window.requestAnimationFrame(tick)
}
